import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ModerationActions from '../components/ModerationActions';
import ReportStatistics from '../components/ReportStatistics';
import ModerationHistory from '../components/ModerationHistory';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import {
  LayoutDashboard,
  Users,
  MapPin,
  TrendingUp,
  Check,
  X,
  Trash2,
  UserPlus,
  Mail,
  Shield,
  Clock,
  Crown,
  MessageSquare,
  BarChart3,
  Zap,
  Star,
  Eye,
  UserCog,
  Heart,
  Image as ImageIcon,
  Flag
} from 'lucide-react';
import api from '../api/axios';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { favoritesCount } = useFavorites();
  const [stats, setStats] = useState({ 
    totalOffers: 0, 
    activeUsers: 0, 
    pendingOffers: 0, 
    approvedThisMonth: 0,
    totalComments: 0,
    totalFavorites: 0,
    offersWithImages: 0
  });
  const [offers, setOffers] = useState([]);
  const [users, setUsers] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [usersFavorites, setUsersFavorites] = useState([]);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (!authLoading) {
      if (!user || !['owner', 'admin', 'moderator'].includes(user.role)) {
        navigate('/');
      } else {
        loadData();
      }
    }
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    // Charger d'abord les données critiques (stats + offers + users) en parallèle
    // pour rendre le Dashboard rapidement, puis charger le reste en arrière-plan
    try {
      const results = await Promise.allSettled([
        api.get('/stats'),
        api.get('/offers/all'),
        api.get('/users'),
      ]);
      if (results[0].status === 'fulfilled') setStats(results[0].value.data);
      if (results[1].status === 'fulfilled') setOffers(results[1].value.data);
      if (results[2].status === 'fulfilled') setUsers(results[2].value.data);

      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length === results.length) {
        toast.error('Erreur', { description: 'Impossible de charger les données' });
      }
    } catch (error) {
      console.error('Error loading critical data:', error);
      toast.error('Erreur', { description: 'Impossible de charger les données' });
    } finally {
      setLoading(false);
    }

    // Charger en arrière-plan les données secondaires (souvent lentes)
    api.get('/users-favorites')
      .then(res => setUsersFavorites(res.data))
      .catch(err => console.warn('users-favorites failed:', err?.message));
    api.get('/reports')
      .then(res => setReports(res.data))
      .catch(err => console.warn('reports failed:', err?.message));
  };

  const handleApprove = async (offerId) => {
    try {
      await api.patch(`/offers/${offerId}/approve`);
      setOffers(prev => prev.map(o => o.id === offerId ? { ...o, status: 'approved' } : o));
      toast.success('Offre approuvée', { description: "L'offre est maintenant visible publiquement" });
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur lors de l\'approbation' });
    }
  };

  const handleReject = async (offerId) => {
    try {
      await api.patch(`/offers/${offerId}/reject`);
      setOffers(prev => prev.map(o => o.id === offerId ? { ...o, status: 'rejected' } : o));
      toast.error('Offre refusée', { description: "L'offre a été refusée" });
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur lors du refus' });
    }
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette offre ?')) return;
    
    try {
      await api.delete(`/offers/${offerId}`);
      setOffers(prev => prev.filter(o => o.id !== offerId));
      toast.success('Offre supprimée', { description: "L'offre a été supprimée définitivement" });
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === user.id) {
      toast.error('Erreur', { description: 'Vous ne pouvez pas vous supprimer vous-même' });
      return;
    }
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Utilisateur supprimé', { description: "L'utilisateur a été supprimé" });
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail || !newUserName || !newUserPassword) {
      toast.error('Erreur', { description: 'Veuillez remplir tous les champs' });
      return;
    }
    
    try {
      await api.post(`/users?role=${newUserRole}`, {
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword
      });
      toast.success('Utilisateur ajouté', { description: `Nouveau ${newUserRole} ajouté avec succès` });
      setNewUserEmail('');
      setNewUserName('');
      setNewUserPassword('');
      setNewUserRole('user');
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur lors de l\'ajout' });
    }
  };

  const handleGrantBooster = async (offerId, type) => {
    if (user.role !== 'owner') {
      toast.error('Accès refusé', { description: 'Seul le propriétaire peut offrir des boosters' });
      return;
    }

    try {
      await api.post('/boosters/grant', {
        user_id: user.id,
        offer_id: offerId,
        booster_type: type,
        duration_days: 7,
        granted_by: user.id
      });
      
      const typeLabel = type === 'boost' ? 'Booster' : 'Mise en avant';
      toast.success(`${typeLabel} activé`, { 
        description: `L'offre sera ${type === 'boost' ? 'boostée' : 'mise en avant'} pendant 7 jours` 
      });
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur lors de l\'activation' });
    }
  };

  const handleRemoveBooster = async (offerId) => {
    if (user.role !== 'owner') return;

    try {
      await api.delete(`/boosters/remove/${offerId}`);
      toast.success('Booster retiré', { description: 'Le booster a été retiré de l\'offre' });
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur' });
    }
  };

  const handlePromoteUser = async (userId, newRole) => {
    if (user.role !== 'owner') {
      toast.error('Accès refusé', { description: 'Seul le propriétaire peut gérer les rôles' });
      return;
    }

    try {
      await api.patch(`/users/${userId}/role`, { role: newRole });
      toast.success('Rôle modifié', { description: `L'utilisateur a été promu ${newRole}` });
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur' });
    }
  };

  const handleRevokeRole = async (userId) => {
    if (user.role !== 'owner') return;

    try {
      await api.patch(`/users/${userId}/role`, { role: 'user' });
      toast.success('Rôle révoqué', { description: 'L\'utilisateur est maintenant un utilisateur standard' });
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Erreur' });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-100 to-teal-200">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-2xl text-gray-600">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!user || !['owner', 'admin', 'moderator'].includes(user.role)) {
    return null;
  }

  const pendingOffers = offers.filter(o => o.status === 'pending');
  const approvedOffers = offers.filter(o => o.status === 'approved');
  const rejectedOffers = offers.filter(o => o.status === 'rejected');

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-100 to-teal-200">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 via-red-500 to-yellow-500 rounded-xl flex items-center justify-center">
                <LayoutDashboard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-800">Tableau de Bord</h1>
                <p className="text-gray-600">Gestion complète de la plateforme</p>
              </div>
            </div>
            {user.role === 'owner' && (
              <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                <Crown className="w-3 h-3 mr-1" />
                Propriétaire
              </Badge>
            )}
          </div>

          {/* Stats - CLIQUABLES */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card 
              className="border-none shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => setActiveTab('approved')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white">Total Offres</CardTitle>
                <MapPin className="w-4 h-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalOffers}</div>
                <p className="text-xs text-purple-100">Cliquez pour voir →</p>
              </CardContent>
            </Card>

            <Card 
              className="border-none shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => setActiveTab('users')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white">Utilisateurs Actifs</CardTitle>
                <Users className="w-4 h-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.activeUsers}</div>
                <p className="text-xs text-green-100">Cliquez pour gérer →</p>
              </CardContent>
            </Card>

            <Card 
              className="border-none shadow-lg bg-gradient-to-br from-orange-500 to-red-600 text-white cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => setActiveTab('pending')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white">En Attente</CardTitle>
                <Clock className="w-4 h-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.pendingOffers}</div>
                <p className="text-xs text-orange-100">Cliquez pour valider →</p>
              </CardContent>
            </Card>

            <Card 
              className="border-none shadow-lg bg-gradient-to-br from-red-500 to-yellow-500 text-white cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => navigate('/analytics')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white">Analyses</CardTitle>
                <BarChart3 className="w-4 h-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">📊</div>
                <p className="text-xs text-yellow-100">Voir statistiques →</p>
              </CardContent>
            </Card>
          </div>

          {/* Nouvelles Stats - Commentaires, Favoris, Images */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card 
              className="border-none shadow-lg bg-gradient-to-br from-pink-500 to-red-500 text-white cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => setActiveTab('comments')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white">Total Commentaires</CardTitle>
                <MessageSquare className="w-4 h-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalComments || 0}</div>
                <p className="text-xs text-pink-100">Cliquez pour voir →</p>
              </CardContent>
            </Card>

            <Card 
              className="border-none shadow-lg bg-gradient-to-br from-orange-500 to-red-600 text-white cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => setActiveTab('reports')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white">Signalements</CardTitle>
                <Flag className="w-4 h-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{reports.filter(r => r.status === 'pending').length}</div>
                <p className="text-xs text-orange-100">En attente →</p>
              </CardContent>
            </Card>

            <Card 
              className="border-none shadow-lg bg-gradient-to-br from-red-400 to-pink-600 text-white cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => setActiveTab('favorites-users')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white">Favoris Utilisateurs</CardTitle>
                <Heart className="w-4 h-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalFavorites}</div>
                <p className="text-xs text-red-100">Voir le classement →</p>
              </CardContent>
            </Card>

            <Card 
              className="border-none shadow-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white cursor-pointer hover:scale-105 transition-transform duration-300"
              onClick={() => setActiveTab('images')}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white">Offres avec Images</CardTitle>
                <ImageIcon className="w-4 h-4" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.offersWithImages || 0}</div>
                <p className="text-xs text-blue-100">Cliquez pour voir →</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Button 
              onClick={() => navigate('/messages')}
              className="h-16 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-lg"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Messagerie (Bientôt disponible)
            </Button>
            <Button 
              onClick={() => navigate('/analytics')}
              className="h-16 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-lg"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              Analyses Détaillées (En développement)
            </Button>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-10">
              <TabsTrigger value="pending">En Attente ({pendingOffers.length})</TabsTrigger>
              <TabsTrigger value="approved">Approuvées ({approvedOffers.length})</TabsTrigger>
              <TabsTrigger value="rejected">Refusées ({rejectedOffers.length})</TabsTrigger>
              <TabsTrigger value="users">Utilisateurs ({users.length})</TabsTrigger>
              <TabsTrigger value="comments">Commentaires</TabsTrigger>
              <TabsTrigger value="reports">Signalements ({reports.filter(r => r.status === 'pending').length})</TabsTrigger>
              <TabsTrigger value="stats-reports">📊 Stats</TabsTrigger>
              {user?.role === 'owner' && <TabsTrigger value="history">📜 Historique</TabsTrigger>}
              <TabsTrigger value="images">Avec Images ({offers.filter(o => o.image).length})</TabsTrigger>
              <TabsTrigger value="favorites-users">Top Favoris</TabsTrigger>
            </TabsList>

            {/* Pending Offers */}
            <TabsContent value="pending" className="space-y-4">
              {pendingOffers.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    Aucune offre en attente de validation
                  </CardContent>
                </Card>
              ) : (
                pendingOffers.map((offer) => (
                  <Card key={offer.id} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                    <CardHeader className="cursor-pointer" onClick={() => navigate(`/offre/${offer.id}`)}>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="hover:text-purple-600 transition-colors">{offer.title}</CardTitle>
                          <CardDescription className="mt-2">{offer.description}</CardDescription>
                        </div>
                        <Badge variant="outline" className="text-orange-600 border-orange-600">
                          En attente
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                        <MapPin className="w-4 h-4" />
                        {offer.address}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          onClick={() => navigate(`/offre/${offer.id}`)}
                          variant="outline"
                          size="sm"
                          className="border-purple-500 text-purple-600 hover:bg-purple-50"
                        >
                          <Eye className="w-3 h-3 mr-2" />
                          Voir Détails
                        </Button>
                        <Button
                          onClick={() => navigate('/messages')}
                          variant="outline"
                          size="sm"
                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          <MessageSquare className="w-3 h-3 mr-2" />
                          Contacter
                        </Button>
                        <Button
                          onClick={() => handleApprove(offer.id)}
                          className="bg-emerald-500 hover:bg-emerald-600"
                          size="sm"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Approuver
                        </Button>
                        <Button
                          onClick={() => handleReject(offer.id)}
                          variant="destructive"
                          size="sm"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Refuser
                        </Button>
                        <Button
                          onClick={() => handleDelete(offer.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* Approved Offers */}
            <TabsContent value="approved" className="space-y-4">
              {approvedOffers.map((offer) => (
                <Card key={offer.id} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader className="cursor-pointer" onClick={() => navigate(`/offre/${offer.id}`)}>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="hover:text-purple-600 transition-colors">{offer.title}</CardTitle>
                        <CardDescription className="mt-2">{offer.description}</CardDescription>
                      </div>
                      <Badge className="bg-emerald-500">Approuvée</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      <MapPin className="w-4 h-4" />
                      {offer.address}
                    </div>
                    
                    {/* Booster badges */}
                    <div className="flex gap-2 mb-4">
                      {offer.boosted && (
                        <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
                          <Zap className="w-3 h-3 mr-1" />
                          Boosté
                        </Badge>
                      )}
                      {offer.featured && (
                        <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                          <Star className="w-3 h-3 mr-1" />
                          En vedette
                        </Badge>
                      )}
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <Button
                        onClick={() => navigate(`/offre/${offer.id}`)}
                        variant="outline"
                        size="sm"
                        className="border-purple-500 text-purple-600 hover:bg-purple-50"
                      >
                        <Eye className="w-3 h-3 mr-2" />
                        Voir Détails
                      </Button>
                      
                      <Button
                        onClick={() => navigate('/messages')}
                        variant="outline"
                        size="sm"
                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                      >
                        <MessageSquare className="w-3 h-3 mr-2" />
                        Envoyer un message
                      </Button>

                      {/* Owner-only booster buttons */}
                      {user?.role === 'owner' && (
                        <>
                          {!offer.boosted && !offer.featured && (
                            <>
                              <Button
                                onClick={() => handleGrantBooster(offer.id, 'boost')}
                                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                                size="sm"
                              >
                                <Zap className="w-3 h-3 mr-2" />
                                Booster (7j)
                              </Button>
                              <Button
                                onClick={() => handleGrantBooster(offer.id, 'featured')}
                                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                                size="sm"
                              >
                                <Star className="w-3 h-3 mr-2" />
                                Mettre en vedette (7j)
                              </Button>
                            </>
                          )}
                          {(offer.boosted || offer.featured) && (
                            <Button
                              onClick={() => handleRemoveBooster(offer.id)}
                              variant="outline"
                              size="sm"
                              className="border-orange-500 text-orange-600 hover:bg-orange-50"
                            >
                              <X className="w-3 h-3 mr-2" />
                              Retirer booster
                            </Button>
                          )}
                        </>
                      )}
                      
                      <Button
                        onClick={() => handleDelete(offer.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* Rejected Offers */}
            <TabsContent value="rejected" className="space-y-4">
              {rejectedOffers.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    Aucune offre refusée
                  </CardContent>
                </Card>
              ) : (
                rejectedOffers.map((offer) => (
                  <Card key={offer.id} className="border-none shadow-lg opacity-60 hover:opacity-100 hover:shadow-xl transition-all">
                    <CardHeader className="cursor-pointer" onClick={() => navigate(`/offre/${offer.id}`)}>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="hover:text-purple-600 transition-colors">{offer.title}</CardTitle>
                          <CardDescription className="mt-2">{offer.description}</CardDescription>
                        </div>
                        <Badge variant="destructive">Refusée</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => navigate(`/offre/${offer.id}`)}
                          variant="outline"
                          size="sm"
                          className="border-purple-500 text-purple-600 hover:bg-purple-50"
                        >
                          <Eye className="w-3 h-3 mr-2" />
                          Voir Détails
                        </Button>
                        <Button
                          onClick={() => navigate('/messages')}
                          variant="outline"
                          size="sm"
                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          <MessageSquare className="w-3 h-3 mr-2" />
                          Expliquer le refus
                        </Button>
                        <Button
                          onClick={() => handleDelete(offer.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer définitivement
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* Users Management */}
            <TabsContent value="users" className="space-y-4">
              {/* Add User (Owner/Admin only) */}
              {['owner', 'admin'].includes(user.role) && (
                <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-blue-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus className="w-5 h-5" />
                      Ajouter un Utilisateur
                    </CardTitle>
                    <CardDescription>
                      {user.role === 'owner' ? 'Vous pouvez créer tous types d\'utilisateurs' : 'Vous pouvez créer des utilisateurs et modérateurs'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAddUser} className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="text"
                          placeholder="Nom complet"
                          value={newUserName}
                          onChange={(e) => setNewUserName(e.target.value)}
                          required
                        />
                        <Input
                          type="email"
                          placeholder="Email"
                          value={newUserEmail}
                          onChange={(e) => setNewUserEmail(e.target.value)}
                          required
                        />
                      </div>
                      <Input
                        type="password"
                        placeholder="Mot de passe"
                        value={newUserPassword}
                        onChange={(e) => setNewUserPassword(e.target.value)}
                        required
                      />
                      <div className="flex gap-3">
                        <Select value={newUserRole} onValueChange={setNewUserRole}>
                          <SelectTrigger className="w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">Utilisateur</SelectItem>
                            <SelectItem value="moderator">Modérateur</SelectItem>
                            {user.role === 'owner' && <SelectItem value="admin">Administrateur</SelectItem>}
                          </SelectContent>
                        </Select>
                        <Button type="submit" className="bg-gradient-to-r from-purple-600 to-blue-500">
                          <UserPlus className="w-4 h-4 mr-2" />
                          Ajouter
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Users List */}
              {users.map((u) => (
                <Card key={u.id} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={u.avatar} />
                          <AvatarFallback>{u.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <CardTitle className="text-lg">{u.name}</CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <Mail className="w-3 h-3 text-gray-500" />
                            <CardDescription>{u.email}</CardDescription>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge
                          className="capitalize"
                          variant={u.role === 'owner' ? 'default' : 'secondary'}
                        >
                          <Shield className="w-3 h-3 mr-1" />
                          {u.role === 'owner' ? '👑 Propriétaire' :
                           u.role === 'admin' ? 'Admin' :
                           u.role === 'moderator' ? 'Modérateur' : 'Utilisateur'}
                        </Badge>
                        
                        {/* Bouton Voir le profil pour tous */}
                        <Button
                          onClick={() => navigate(`/profil/${u.id}`)}
                          variant="outline"
                          size="sm"
                          className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                        >
                          <Eye className="w-3 h-3 mr-2" />
                          Voir profil
                        </Button>
                        
                        {/* Owner-only role management */}
                        {user.role === 'owner' && u.id !== user.id && (
                          <div className="flex gap-2">
                            {u.role === 'user' && (
                              <>
                                <Button
                                  onClick={() => handlePromoteUser(u.id, 'moderator')}
                                  variant="outline"
                                  size="sm"
                                  className="border-purple-500 text-purple-600 hover:bg-purple-50"
                                >
                                  <UserCog className="w-3 h-3 mr-2" />
                                  Modérateur
                                </Button>
                                <Button
                                  onClick={() => handlePromoteUser(u.id, 'admin')}
                                  variant="outline"
                                  size="sm"
                                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                >
                                  <Crown className="w-3 h-3 mr-2" />
                                  Administrateur
                                </Button>
                              </>
                            )}
                            
                            {(u.role === 'moderator' || u.role === 'admin') && (
                              <>
                                <Button
                                  onClick={() => handleRevokeRole(u.id)}
                                  variant="outline"
                                  size="sm"
                                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                                >
                                  <X className="w-3 h-3 mr-2" />
                                  Révoquer
                                </Button>
                                {u.role === 'moderator' && (
                                  <Button
                                    onClick={() => handlePromoteUser(u.id, 'admin')}
                                    variant="outline"
                                    size="sm"
                                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                  >
                                    <Crown className="w-3 h-3 mr-2" />
                                    Promouvoir Admin
                                  </Button>
                                )}
                              </>
                            )}
                            
                            <Button
                              onClick={() => handleDeleteUser(u.id)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </TabsContent>

            {/* Comments Tab */}
            <TabsContent value="comments" className="space-y-4">
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Tous les Commentaires
                  </CardTitle>
                  <CardDescription>
                    Gérez les avis et commentaires sur les offres
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-lg font-medium mb-2">Fonctionnalité en développement</p>
                    <p className="text-sm">Les commentaires seront affichés ici prochainement</p>
                    <Button 
                      onClick={() => navigate('/carte')}
                      className="mt-4 bg-gradient-to-r from-purple-600 to-pink-500"
                    >
                      Voir les offres avec commentaires
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reports Tab - Signalements */}
            <TabsContent value="reports" className="space-y-4">
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Flag className="w-5 h-5 text-red-500" />
                    Signalements d'Offres
                  </CardTitle>
                  <CardDescription>
                    Gérez les signalements des utilisateurs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {reports.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Flag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Aucun signalement pour le moment</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {reports.map((report) => (
                        <Card key={report.id} className="border border-orange-200 bg-orange-50">
                          <CardContent className="pt-6">
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-lg">{report.offer_title}</h4>
                                  <Badge 
                                    variant="outline"
                                    className={
                                      report.status === 'pending' ? 'border-orange-500 text-orange-600' :
                                      report.status === 'reviewed' ? 'border-blue-500 text-blue-600' :
                                      'border-green-500 text-green-600'
                                    }
                                  >
                                    {report.status === 'pending' ? '⏳ En attente' :
                                     report.status === 'reviewed' ? '👁️ Examiné' : '✅ Résolu'}
                                  </Badge>
                                </div>
                                <div className="space-y-2 text-sm">
                                  <p><strong>Raison :</strong> {report.reason}</p>
                                  {report.description && (
                                    <p><strong>Description :</strong> {report.description}</p>
                                  )}
                                  <p className="text-gray-500">
                                    <strong>Signalé par :</strong> {report.reporter_name} • 
                                    <strong> Le :</strong> {new Date(report.created_at).toLocaleDateString('fr-FR')}
                                  </p>
                                </div>
                              </div>
                            </div>
                            {/* Moderation Actions */}
                            <ModerationActions 
                              report={report} 
                              onActionComplete={loadData}
                            />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Statistics Tab - Report Analytics */}
            <TabsContent value="stats-reports" className="space-y-4">
              <ReportStatistics />
            </TabsContent>

            {/* History Tab - Full Moderation History (Owner Only) */}
            {user?.role === 'owner' && (
              <TabsContent value="history" className="space-y-4">
                <ModerationHistory />
              </TabsContent>
            )}

            {/* Images Tab - Offers with Images */}
            <TabsContent value="images" className="space-y-4">
              {offers.filter(o => o.image).length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucune offre avec image pour le moment</p>
                  </CardContent>
                </Card>
              ) : (
                offers.filter(o => o.image).map((offer) => (
                  <Card key={offer.id} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                    <CardHeader className="cursor-pointer" onClick={() => navigate(`/offre/${offer.id}`)}>
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex gap-4 flex-1">
                          {offer.image && (
                            <img 
                              src={offer.image} 
                              alt={offer.title}
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                          )}
                          <div className="flex-1">
                            <CardTitle className="hover:text-purple-600 transition-colors">{offer.title}</CardTitle>
                            <CardDescription className="mt-2">{offer.description}</CardDescription>
                          </div>
                        </div>
                        <Badge 
                          variant="outline" 
                          className={
                            offer.status === 'approved' ? 'text-green-600 border-green-600' :
                            offer.status === 'pending' ? 'text-orange-600 border-orange-600' :
                            'text-red-600 border-red-600'
                          }
                        >
                          {offer.status === 'approved' ? 'Approuvée' :
                           offer.status === 'pending' ? 'En attente' : 'Refusée'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                        <MapPin className="w-4 h-4" />
                        {offer.address}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          onClick={() => navigate(`/offre/${offer.id}`)}
                          variant="outline"
                          size="sm"
                          className="border-purple-500 text-purple-600 hover:bg-purple-50"
                        >
                          <Eye className="w-3 h-3 mr-2" />
                          Voir Détails
                        </Button>
                        <Button
                          onClick={() => handleDelete(offer.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3 mr-2" />
                          Supprimer
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* Favorites Users Tab */}
            <TabsContent value="favorites-users" className="space-y-4">
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="w-5 h-5 text-red-500" />
                    Classement des Utilisateurs par Favoris
                  </CardTitle>
                  <CardDescription>
                    Utilisateurs classés par nombre de favoris
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {usersFavorites.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Heart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Aucun favori pour le moment</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {usersFavorites.map((userFav, index) => (
                        <Card key={userFav.id} className="border-none shadow-md hover:shadow-lg transition-shadow">
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-3">
                                  <div 
                                    className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg"
                                  >
                                    {index + 1}
                                  </div>
                                  <Avatar className="w-12 h-12">
                                    <AvatarImage src={userFav.avatar} />
                                    <AvatarFallback>{userFav.name.charAt(0)}</AvatarFallback>
                                  </Avatar>
                                </div>
                                <div>
                                  <p className="font-semibold text-lg">{userFav.name}</p>
                                  <p className="text-sm text-gray-500">{userFav.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <div className="flex items-center gap-2">
                                    <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                                    <span className="text-2xl font-bold text-red-500">
                                      {userFav.favorites_count}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500">favoris</p>
                                </div>
                                <Button
                                  onClick={() => navigate(`/profil/${userFav.id}`)}
                                  variant="outline"
                                  size="sm"
                                  className="border-purple-500 text-purple-600 hover:bg-purple-50"
                                >
                                  <Eye className="w-3 h-3 mr-2" />
                                  Voir profil
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;
